import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  LifeBuoy,
  ChevronDown,
  Shield,
  CreditCard,
  Camera,
  Volume2,
  Trash2,
  Apple,
} from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useReaderAppMode } from "../lib/readerAppMode";

const SUPPORT_EMAIL = "support@trubeaute.com";

// Stable identity map. The keys here are FAQ IDs used for test selectors
// and reader-app filtering. The matching translation key lives in
// `support.faq.<i18nKey>` in each locale file.
const FAQ_MAP = [
  { id: "what-is", i18nKey: "whatIs", icon: LifeBuoy, hasLinks: false },
  { id: "how-privacy", i18nKey: "howPrivacy", icon: Shield, hasLinks: true },
  { id: "camera-perm", i18nKey: "cameraPerm", icon: Camera, hasLinks: false },
  { id: "voice", i18nKey: "voice", icon: Volume2, hasLinks: true },
  { id: "billing", i18nKey: "billing", icon: CreditCard, hasLinks: false },
  { id: "ios-purchase", i18nKey: "iosPurchase", icon: Apple, hasLinks: false },
  { id: "delete", i18nKey: "delete", icon: Trash2, hasLinks: true },
  { id: "refund", i18nKey: "refund", icon: CreditCard, hasLinks: true },
  { id: "bug", i18nKey: "bug", icon: LifeBuoy, hasLinks: true },
];

const transComponents = {
  strong: <span className="font-medium" />,
  code: <span className="font-mono text-xs bg-[#F2E8E9] px-1.5 py-0.5 rounded" />,
  privacy: <Link to="/privacy" className="text-[#9B6A6C] underline" />,
  settings: <Link to="/settings" className="text-[#9B6A6C] underline" />,
  mail: (
    <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#9B6A6C] underline" />
  ),
};

const FAQItem = ({ faq, open, onToggle }) => {
  const { t } = useTranslation();
  const Icon = faq.icon;
  const qKey = `support.faq.${faq.i18nKey}.q`;
  const aKey = `support.faq.${faq.i18nKey}.a`;
  return (
    <div
      className="bg-white/80 backdrop-blur-xl border border-[#EADDD7] rounded-2xl overflow-hidden"
      data-testid={`support-faq-${faq.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-5 py-4 flex items-start gap-3 text-left hover:bg-[#FDFBF7] transition-colors"
        aria-expanded={open}
        data-testid={`support-faq-toggle-${faq.id}`}
      >
        <span className="shrink-0 h-8 w-8 rounded-full bg-[#F2E8E9] flex items-center justify-center mt-0.5">
          <Icon className="h-4 w-4 text-[#9B6A6C]" />
        </span>
        <span className="flex-1 font-medium text-[#2C2123] text-base leading-snug">
          {t(qKey)}
        </span>
        <ChevronDown
          className={`h-5 w-5 text-[#8A7D82] shrink-0 mt-0.5 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div
          className="px-5 pb-5 pt-1 pl-[60px] text-[#5C4B51] leading-relaxed text-sm"
          data-testid={`support-faq-body-${faq.id}`}
        >
          {faq.hasLinks ? (
            <Trans i18nKey={aKey} components={transComponents} />
          ) : (
            t(aKey)
          )}
        </div>
      )}
    </div>
  );
};

const Support = () => {
  const { t } = useTranslation();
  const readerApp = useReaderAppMode();
  const [openId, setOpenId] = useState(null);
  // Reader-app mode: hide the billing / iOS purchase / refund FAQs so
  // the iOS build stays clean of any pricing references per Apple
  // Guideline 3.1.3.
  const visibleFaqs = useMemo(
    () =>
      readerApp
        ? FAQ_MAP.filter(
            (f) => !["billing", "ios-purchase", "refund"].includes(f.id)
          )
        : FAQ_MAP,
    [readerApp]
  );

  return (
    <div className="min-h-screen trubeaute-gradient">
      <Header />
      <main
        className="pt-32 pb-16 px-6 max-w-3xl mx-auto"
        data-testid="support-page"
      >
        <div className="text-center mb-10">
          <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#9B6A6C] mb-3">
            {t("support.kicker")}
          </span>
          <h1 className="text-4xl sm:text-5xl font-light text-[#2C2123] leading-tight mb-4">
            {t("support.headlineBefore")}{" "}
            <span className="font-serif italic text-[#5D4050]">
              {t("support.headlineEmphasis")}
            </span>
            ?
          </h1>
          <p className="text-[#5C4B51] max-w-xl mx-auto">
            {t("support.intro")}
          </p>
        </div>

        <div className="space-y-3">
          {visibleFaqs.map((faq) => (
            <FAQItem
              key={faq.id}
              faq={faq}
              open={openId === faq.id}
              onToggle={() =>
                setOpenId((cur) => (cur === faq.id ? null : faq.id))
              }
            />
          ))}
        </div>

        <div
          className="mt-12 bg-[#FDFBF7] border border-[#EADDD7] rounded-3xl p-8 text-center"
          data-testid="support-contact-card"
        >
          <div className="inline-flex h-12 w-12 rounded-full bg-[#F2E8E9] items-center justify-center mb-4">
            <Mail className="h-5 w-5 text-[#9B6A6C]" />
          </div>
          <h2 className="text-xl font-medium text-[#2C2123] mb-2">
            {t("support.stillStuck")}
          </h2>
          <p className="text-[#5C4B51] mb-4 text-sm">
            {t("support.stillStuckBody")}
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="inline-flex items-center gap-2 bg-[#9B6A6C] text-white rounded-full px-6 py-3 text-sm hover:bg-[#805557] transition-colors"
            data-testid="support-email-cta"
          >
            <Mail className="h-4 w-4" />
            {SUPPORT_EMAIL}
          </a>
          <p className="text-xs text-[#8A7D82] mt-6">
            {t("support.privacyQuestionsBefore")}{" "}
            <Link to="/privacy" className="text-[#9B6A6C] underline">
              {t("support.privacyLink")}
            </Link>{" "}
            ·{" "}
            <Link to="/terms" className="text-[#9B6A6C] underline">
              {t("support.termsLink")}
            </Link>{" "}
            ·{" "}
            <Link to="/contact" className="text-[#9B6A6C] underline">
              {t("support.partnershipsLink")}
            </Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Support;
